// src/components/ResourceDisplay.tsx
import React, { useEffect, useState } from 'react';
import styles from './ResourceDisplay.module.css';

interface ResourceDisplayProps {
  folderName: string;
}

const ResourceDisplay: React.FC<ResourceDisplayProps> = ({ folderName }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

   // Helper to format a date as e.g. "Sunday 9th November"
  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    };
    const formatted = date.toLocaleDateString('en-GB', options);
    const day = date.getUTCDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
        ? 'rd'
        : 'th';
    return formatted.replace(/\d+/, `${day}${suffix}`);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load woking.txt
        const textRes = await fetch(`/resources/${folderName}/woking.txt`);
        let textData = await textRes.text();

        // Load dates.txt
        const datesRes = await fetch(`/resources/${folderName}/dates.txt`);
        const dateLines = (await datesRes.text())
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean);

        const now = new Date();
        const nowUTC = new Date(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          now.getUTCHours(),
          now.getUTCMinutes(),
          now.getUTCSeconds()
        );

        // Parse all future event dates
        const dates = dateLines.map((line) => new Date(`${line}T00:00:00Z`));

        // Find next date
        let nextDate: Date | null = null;
        for (let i = 0; i < dates.length; i++) {
          const eventDate = dates[i];
          const eventEnd = new Date(eventDate);
          eventEnd.setUTCHours(14, 0, 0, 0); // 2pm UTC

          if (nowUTC < eventEnd) {
            nextDate = eventDate;
            break;
          }
        }

        if (!nextDate && dates.length > 0) {
          nextDate = dates[dates.length - 1]; // fallback
        }

        // Replace the {{DATE}} placeholder
        if (nextDate) {
          textData = textData.replace(/\{\{DATE\}\}/g, formatDate(nextDate));
        }

        // Separate title and body
        const lines = textData.split('\n');
        setTitle(lines[0]);
        setBody(lines.slice(1).join('\n'));
      } catch (err) {
        console.error('Error loading text file:', err);
      }
    };

    loadData();
  }, [folderName]);

  return (
    <div className={styles.contentBox}>
      <div className={styles.textBox}>
        <img
          src="/logo192.png"
          alt="Woking Car Boot Logo"
          className={styles.logo}
        />
        <div
          className={styles.title}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <div
          className={styles.body}
          dangerouslySetInnerHTML={{ __html: body }}
        />
      </div>
      <div className={styles.imageWrapper}>
        <img
          src={`/resources/${folderName}/woking.jpg`}
          alt={`Car Boot ${folderName}`}
          className={styles.image}
        />
      </div>
      <div className={styles.mapWrapper}>
        <iframe
          title="Woking Carboot Location"
          src="https://www.google.com/maps?q=51.30956591850814, -0.5553103705823833&hl=en&z=17&output=embed"
          width="100%"
          height="300"
          allowFullScreen={true}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
    </div>
  );
};

export default ResourceDisplay;
